import { handicapAPI } from '@/services/handicap'

/**
 * Enriquece los scores con información sobre si fueron incluidos en el cálculo del handicap
 * Algoritmo USGA simplificado: 8 mejores diferenciales de las últimas 20 rondas
 */
function enrichScoresWithHandicapInfo (scores) {
  if (!Array.isArray(scores) || scores.length === 0) {
    return scores
  }

  // Tomar últimas 20 rondas ordenadas por fecha descendente (más recientes primero)
  const recentScores = [...scores].slice(0, 20)

  // Calcular diferencial para cada ronda (score bruto - par de la cancha)
  const scoresWithDiff = recentScores.map(score => {
    const summary = score.summary || {}
    const totalPar = summary.total_par || 72
    const totalScore = summary.total_score || score.total_score || 0
    const differential = totalScore - totalPar

    return {
      ...score,
      differential,
      handicapIndex: -1,
      isIncludedInHandicap: false
    }
  })

  // Ordenar por diferencial ASC (mejores primero)
  const sorted = [...scoresWithDiff].sort((a, b) => a.differential - b.differential)

  // Marcar los 8 mejores
  const topEight = sorted.slice(0, 8)
  topEight.forEach((score, idx) => {
    score.handicapIndex = idx + 1
    score.isIncludedInHandicap = true
  })

  // Devolver en orden original (más recientes primero)
  return scoresWithDiff.map(score => {
    const inTopEight = topEight.find(t => t.id === score.id)
    if (inTopEight) {
      score.handicapIndex = inTopEight.handicapIndex
      score.isIncludedInHandicap = inTopEight.isIncludedInHandicap
    }
    return score
  })
}

const state = {
  myHandicap: null,
  allHandicaps: [],
  userScores: [],
  selectedUserScores: {},
  scorecard: null,
  auditLogs: [],
  loading: false,
  error: null,
  lastFetch: null
}

const getters = {
  myHandicapValue: (state) =>
    state.myHandicap?.official_handicap || null,

  getHandicapById: (state) => (userId) =>
    state.allHandicaps.find(h => h.user === userId),

  getUserScoresCount: (state) => (userId) =>
    state.selectedUserScores[userId]?.length || 0,

  sortedHandicaps: (state) =>
    [...state.allHandicaps].sort((a, b) =>
      (a.official_handicap || 0) - (b.official_handicap || 0)
    ),

  clubAverageHandicap: (state) => {
    if (!state.allHandicaps.length) return 0
    const sum = state.allHandicaps.reduce((acc, h) => acc + (h.official_handicap || 0), 0)
    return (sum / state.allHandicaps.length).toFixed(1)
  },

  bestHandicapInClub: (state) => {
    if (!state.allHandicaps.length) return null
    return state.allHandicaps.reduce((best, h) =>
      (h.official_handicap || 54) < (best.official_handicap || 54) ? h : best
    )
  },

  calculateTrend: (state) => (userId) => {
    if (!state.selectedUserScores[userId]) return null
    const scores = state.selectedUserScores[userId]
    if (scores.length < 2) return null

    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    // Encontrar score de hace 7 días (más cercano)
    const scoresInWindow = scores.filter(s => {
      const scoreDate = new Date(s.created_at)
      return scoreDate >= sevenDaysAgo
    })

    if (scoresInWindow.length === 0) return null

    // Handicap más antiguo en la ventana
    const oldestInWindow = scoresInWindow[scoresInWindow.length - 1]
    const newest = scores[0]

    const oldValue = parseFloat(oldestInWindow.handicap) || 0
    const newValue = parseFloat(newest.handicap) || 0
    const difference = oldValue - newValue // Negativo = mejoró

    return {
      trend: difference < -0.1 ? 'improving' : difference > 0.1 ? 'declining' : 'stable',
      value: Math.abs(difference).toFixed(1),
      direction: difference < -0.1 ? '↘️' : difference > 0.1 ? '↗️' : '→',
      daysWindow: 7
    }
  },

  getPlayerComparison: (state) => (userId) => {
    if (!state.allHandicaps.length) return null

    const player = state.allHandicaps.find(h => h.user === userId)
    if (!player) return null

    const yourHandicap = player.official_handicap || 0

    // Calcular posición (cuántos tienen mejor handicap)
    const betterThanYou = state.allHandicaps.filter(h => (h.official_handicap || 54) < yourHandicap).length
    const totalPlayers = state.allHandicaps.length
    const position = betterThanYou + 1

    // Calcular percentil
    const percentile = Math.round((betterThanYou / totalPlayers) * 100)

    // Promedio del club
    const clubAverage = state.allHandicaps.reduce((acc, h) => acc + (h.official_handicap || 0), 0) / totalPlayers

    // Mejor jugador
    const bestPlayer = state.allHandicaps.reduce((best, h) =>
      (h.official_handicap || 54) < (best.official_handicap || 54) ? h : best
    )

    return {
      yourHandicap: yourHandicap.toFixed(1),
      clubAverage: clubAverage.toFixed(1),
      bestPlayerHandicap: (bestPlayer.official_handicap || 0).toFixed(1),
      bestPlayerName: bestPlayer.user_display,
      position,
      totalPlayers,
      percentile,
      betterThanYou,
      worseThanYou: totalPlayers - position,
      differenceVsAverage: (yourHandicap - clubAverage).toFixed(1)
    }
  }
}

const mutations = {
  SET_MY_HANDICAP (state, handicap) {
    state.myHandicap = handicap
    state.lastFetch = new Date()
  },

  SET_ALL_HANDICAPS (state, handicaps) {
    state.allHandicaps = handicaps
  },

  SET_USER_SCORES (state, { userId, scores }) {
    state.selectedUserScores[userId] = scores
  },

  SET_SCORECARD (state, scorecard) {
    state.scorecard = scorecard
  },

  SET_AUDIT_LOGS (state, logs) {
    state.auditLogs = logs
  },

  SET_LOADING (state, loading) {
    state.loading = loading
  },

  SET_ERROR (state, error) {
    state.error = error
  },

  CLEAR_ERROR (state) {
    state.error = null
  }
}

const actions = {
  async fetchMyHandicap ({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await handicapAPI.getMyHandicap()
      commit('SET_MY_HANDICAP', response.data)
      commit('CLEAR_ERROR')
    } catch (error) {
      // 404 es normal si no hay scores aún
      if (error.response?.status !== 404) {
        commit('SET_ERROR', error.message)
        console.error('Error fetching my handicap:', error)
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAllHandicaps ({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await handicapAPI.list(params)
      const handicaps = response.data.results || response.data
      commit('SET_ALL_HANDICAPS', handicaps)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching all handicaps:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchUserHandicap ({ commit }, userId) {
    try {
      const response = await handicapAPI.getUser(userId)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching handicap for user ${userId}:`, error)
      return null
    }
  },

  async fetchUserScores ({ commit }, { userId, params = {} }) {
    try {
      const response = await handicapAPI.getUserScores(userId, params)
      let scores = response.data.results || response.data

      // Enriquecer con información de si fue incluida en cálculo (8 mejores de últimas 20)
      scores = enrichScoresWithHandicapInfo(scores)

      commit('SET_USER_SCORES', { userId, scores })
      return scores
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching scores for user ${userId}:`, error)
      return []
    }
  },

  async fetchScorecard ({ commit }, scoreId) {
    try {
      const response = await handicapAPI.getScorecard(scoreId)
      commit('SET_SCORECARD', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching scorecard ${scoreId}:`, error)
      return null
    }
  },

  async fetchAuditLogs ({ commit }, params = {}) {
    try {
      const response = await handicapAPI.getAuditLog(params)
      const logs = response.data.results || response.data
      commit('SET_AUDIT_LOGS', logs)
      return logs
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching audit logs:', error)
      return []
    }
  },

  clearError ({ commit }) {
    commit('CLEAR_ERROR')
  },

  reset ({ commit }) {
    commit('SET_MY_HANDICAP', null)
    commit('SET_ALL_HANDICAPS', [])
    commit('SET_USER_SCORES', { userId: null, scores: [] })
    commit('SET_SCORECARD', null)
    commit('SET_AUDIT_LOGS', [])
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
